import { VERDICT_DEFINITIONS, type Verdict } from './scoreDeal';

interface Props {
  verdict: Verdict;
  passed: number;
  total: number;
  profileName: string;
  structureLine: string;
  caveat?: string | null;
}

/**
 * REVIEW gets its own amber treatment rather than being folded into the red
 * "not GO" branch. Defect #8: this card used to receive a two-state verdict that
 * called a 4-of-5 deal GO while the decision card below it said REVIEW. Now both
 * read `scoreDeal`, and a borderline deal has to LOOK borderline here too - a
 * green GO badge on a deal that fails IRR is the exact failure being fixed.
 */
const TONE: Record<Verdict, { wrap: string; badge: string }> = {
  GO: {
    wrap: 'bg-success-wash border-success/30',
    badge: 'text-success border-success',
  },
  // CONDITIONAL and CAUTION share amber because his own palette does - same border, fill and
  // text. They differ in what they mean, not in how they look.
  CONDITIONAL: {
    wrap: 'bg-warning-wash border-warning/30',
    badge: 'text-warning border-warning',
  },
  CAUTION: {
    wrap: 'bg-warning-wash border-warning/30',
    badge: 'text-warning border-warning',
  },
  'NO GO': {
    wrap: 'bg-red-50 border-red-300',
    badge: 'text-red-700 border-red-400',
  },
};

export default function VerdictCard({ verdict, passed, total, profileName, structureLine, caveat }: Props) {
  const tone = TONE[verdict] ?? TONE['NO GO'];
  return (
    <div className={`rounded-xl border p-5 flex items-center gap-5 ${tone.wrap}`}>
      <div className={`px-6 py-4 rounded-lg border text-3xl font-bold ${tone.badge}`}>
        {verdict}
      </div>
      <div className="space-y-1">
        <p className="font-semibold text-gray-900">{profileName} | {passed}/{total} metrics pass</p>
        <p className="text-sm text-gray-700">{structureLine}</p>
        {caveat && <p className="text-sm text-warning">{caveat}</p>}
        {/* His documents print the definition in small italic type under the banner, so the
            status reads as a classification rather than an opinion. */}
        <p className="text-xs italic text-gray-500">{verdict}: {VERDICT_DEFINITIONS[verdict]}</p>
      </div>
    </div>
  );
}
